// view/floatingWindow.js — a window that floats over the page without blocking it: drag it by its title bar,
// minimise it to the bar, restore, close. Built on a non-modal <dialog> (show(), not showModal()), so the page behind
// stays usable — e.g. to take a second screenshot while a report is half written.
// First consumer: the quick report of the message center (bigplan backend/message-center, phase polish, step 5a).
// Stateful component: { element, body, open(state?), close, minimize, restore, isOpen, isMinimized, getState, destroy }.
import '../../../stylesheet_vite/src/modules/floating-window.css';
import { createDisposers } from '../core/disposers.js';

const FRONT_Z = '10000';    // floating-window.css base: the pressed window (step 8e) …
const BEHIND_Z = '9999';    // … the other floating windows; never above 10000, the legacy jQuery UI dialogs (10001) stay on top
const MARGIN = 24;          // default distance from the right edge
const DEFAULT_TOP = 80;     // below the sticky admin header
const KEEP_VISIBLE = 80;    // px of the bar that always stay on screen while dragging
const BAR_HEIGHT = 36;

/** Keeps the window where its bar stays reachable. Pure. */
export function clampPosition({ left, top }, { width }, { vw, vh }) {
    return {
        left: Math.min(Math.max(left, KEEP_VISIBLE - width), vw - KEEP_VISIBLE),
        top: Math.min(Math.max(top, 0), Math.max(0, vh - BAR_HEIGHT)),
    };
}

// onChange(state): after the person dragged, minimised or restored the window — for a consumer that keeps the window's
// state across page changes (the notice window, bigplan backend/message-center step 8d). state = { left, top, minimized }.
export function createFloatingWindow({ title, iconClass = '', className = '', labels = {}, onClose = () => {}, onChange = () => {}, env = window }) {
    const text = { minimize: 'Minimise', restore: 'Restore', close: 'Close', ...labels };
    const disposers = createDisposers();
    let position = null;   // { left, top } once placed; kept between close and open

    const element = document.createElement('dialog');
    element.className = 'floatingWindow' + (className ? ' ' + className : '');
    element.setAttribute('aria-label', title);

    const bar = document.createElement('div');
    bar.className = 'floatingWindow__bar';
    if (iconClass) {
        const icon = document.createElement('span');
        icon.className = 'floatingWindow__icon ' + iconClass;
        bar.append(icon);
    }
    const caption = document.createElement('span');
    caption.className = 'floatingWindow__title';
    caption.textContent = title;
    const minimizeButton = iconButton('topo2-ic--minimize', text.minimize);
    const closeButton = iconButton('topo2-ic--close', text.close);
    bar.append(caption, minimizeButton, closeButton);

    const body = document.createElement('div');
    body.className = 'floatingWindow__body';
    element.append(bar, body);

    listen(minimizeButton, 'click', () => toggleByPerson());
    listen(closeButton, 'click', () => { close(); onClose(); });
    listen(bar, 'dblclick', (event) => { if (!event.target.closest('button')) { toggleByPerson(); } });
    listen(bar, 'pointerdown', startDrag);
    // two windows at once (quick report + notice): the one pressed comes to the front. The legacy jQuery UI dialogs
    // (10001) and the saving overlay stay above — they are modal questions.
    listen(element, 'pointerdown', bringToFront);
    listen(env, 'resize', () => { if (position) { place(position); } });

    function listen(target, event, fn) {
        target.addEventListener(event, fn);
        disposers.add(() => target.removeEventListener(event, fn));
    }

    function iconButton(modifier, label) {
        const button = document.createElement('button');
        button.type = 'button';
        // --inset: icon size and padding of the legacy window's title bar buttons (basic.js TITLEBAR_ICON_BUTTON)
        button.className = 'topo2-btn topo2-btn--solid topo2-btn--icon topo2-btn--extrasmall topo2-btn--inset floatingWindow__button';
        button.title = label;
        button.setAttribute('aria-label', label);
        const icon = document.createElement('span');
        icon.className = 'topo2-ic ' + modifier;
        button.append(icon);
        return button;
    }

    function place(next) {
        position = clampPosition(next, { width: element.offsetWidth || 0 }, { vw: env.innerWidth, vh: env.innerHeight });
        element.style.left = position.left + 'px';
        element.style.top = position.top + 'px';
    }

    function startDrag(event) {
        if (event.button !== 0 || event.target.closest('button')) { return; }
        event.preventDefault();
        const start = { x: event.clientX, y: event.clientY, left: position?.left ?? 0, top: position?.top ?? 0 };
        bar.setPointerCapture?.(event.pointerId);
        element.classList.add('is-dragging');
        const move = (e) => place({ left: start.left + e.clientX - start.x, top: start.top + e.clientY - start.y });
        const stop = () => {
            element.classList.remove('is-dragging');
            bar.removeEventListener('pointermove', move);
            bar.removeEventListener('pointerup', stop);
            bar.removeEventListener('pointercancel', stop);
            onChange(getState());
        };
        bar.addEventListener('pointermove', move);
        bar.addEventListener('pointerup', stop);
        bar.addEventListener('pointercancel', stop);
    }

    function isOpen() {
        return element.hasAttribute('open');
    }

    function isMinimized() {
        return element.classList.contains('is-minimized');
    }

    /**
     * Shows the window (restored); the first time at the top right, later where it was left. A state (getState() of an
     * earlier page) puts it where it was and minimised if it was.
     */
    function open(state = null) {
        if (!isOpen()) {
            if (typeof element.show === 'function') { element.show(); } else { element.setAttribute('open', ''); }
        }
        if (state && Number.isFinite(state.left) && Number.isFinite(state.top)) { position = { left: state.left, top: state.top }; }
        if (state?.minimized) { minimize(); } else { restore(); }
        place(position ?? { left: env.innerWidth - (element.offsetWidth || 0) - MARGIN, top: DEFAULT_TOP });
    }

    function bringToFront() {
        if (element.style.zIndex === FRONT_Z) { return; }
        for (const other of element.ownerDocument.querySelectorAll('.floatingWindow')) {
            if (other !== element) { other.style.zIndex = BEHIND_Z; }
        }
        element.style.zIndex = FRONT_Z;
    }

    /** Where the window is and whether it is minimised — to put it back the same way on the next page. */
    function getState() {
        return { left: position?.left ?? null, top: position?.top ?? null, minimized: isMinimized() };
    }

    // minimise / restore by the person (button, double click): reported, unlike the calls of open()
    function toggleByPerson() {
        if (isMinimized()) { restore(); } else { minimize(); }
        onChange(getState());
    }

    function close() {
        if (typeof element.close === 'function') { element.close(); } else { element.removeAttribute('open'); }
    }

    function minimize() {
        element.classList.add('is-minimized');
        body.hidden = true;
        minimizeButton.title = text.restore;
        minimizeButton.setAttribute('aria-label', text.restore);
        minimizeButton.querySelector('.topo2-ic').className = 'topo2-ic topo2-ic--maximize';
    }

    function restore() {
        element.classList.remove('is-minimized');
        body.hidden = false;
        minimizeButton.title = text.minimize;
        minimizeButton.setAttribute('aria-label', text.minimize);
        minimizeButton.querySelector('.topo2-ic').className = 'topo2-ic topo2-ic--minimize';
    }

    function destroy() {
        disposers.dispose();
        element.remove();
    }

    return { element, body, open, close, minimize, restore, isOpen, isMinimized, getState, destroy };
}
